import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, HeartHandshake, Trophy, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import EventCard from "@/components/EventCard";
import SectionHeader from "./SectionHeader";
import EmptyState from "./EmptyState";

export default function LocalLife() {
  const [events, setEvents] = useState(null);

  useEffect(() => {
    api.get("/public/events?limit=60").then((r) => {
      const list = (r.data || []).slice();
      const byDate = (a, b) => new Date(a.start_date) - new Date(b.start_date);
      const ponctuels = list.filter((e) => !e.is_recurring).sort(byDate);
      const recurrents = list.filter((e) => e.is_recurring).sort(byDate);
      // 8 vignettes : ~80 % événements du jour (ponctuels à venir) + ~20 % récurrents
      const picked = [...ponctuels.slice(0, 6), ...recurrents.slice(0, 2)];
      if (picked.length < 8) picked.push(...ponctuels.slice(6, 6 + (8 - picked.length)));
      setEvents(picked.slice(0, 8));
    }).catch(() => setEvents([]));
  }, []);

  if (events === null) return null;

  return (
    <section className="bg-[#F2F4F5] border-y border-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14" data-testid="local-life-section">
        <SectionHeader
          eyebrow="Vie locale"
          title="Événements, clubs & associations"
          subtitle="Tout ce qui fait vivre votre territoire : marchés, concerts, animations et vie associative."
          linkTo="/sortir"
          linkLabel="Tout l'agenda"
          linkTestId="view-events-btn"
        />

        {events.length === 0 ? (
          <div className="mt-6">
            <EmptyState
              icon={CalendarDays}
              title="Aucun événement à venir pour l'instant"
              text="Organisez-vous un marché, un concert ou une animation ? Proposez votre événement, c'est gratuit."
              ctaLabel="Proposer un événement"
              ctaTo="/proposer-un-evenement"
              testId="local-life-empty"
            />
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4" data-testid="local-life-grid">
            {events.map((e) => (
              <EventCard key={e.id} event={e} compact recurrenceLabel={e.is_recurring ? "Récurrent" : undefined} />
            ))}
          </div>
        )}

        {/* Raccourcis vie associative */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Link to="/associations" data-testid="association-card" className="group rounded-2xl bg-white border border-slate-200 p-4 flex items-center gap-4 hover:border-[#E05638]/30 hover:shadow-sm transition-all">
            <span className="w-11 h-11 rounded-xl bg-[#FFF4EC] text-[#E05638] grid place-items-center shrink-0"><HeartHandshake size={20} /></span>
            <span className="min-w-0 flex-1">
              <span className="block font-heading font-bold text-[#172A3A]">Les associations locales</span>
              <span className="block text-sm text-slate-500">Découvrez et soutenez la vie associative du 28</span>
            </span>
            <ArrowRight size={16} className="text-[#E05638] shrink-0 transition-transform group-hover:translate-x-0.5" />
          </Link>
          <Link to="/clubs" data-testid="club-card" className="group rounded-2xl bg-white border border-slate-200 p-4 flex items-center gap-4 hover:border-[#E05638]/30 hover:shadow-sm transition-all">
            <span className="w-11 h-11 rounded-xl bg-[#FFF4EC] text-[#E05638] grid place-items-center shrink-0"><Trophy size={20} /></span>
            <span className="min-w-0 flex-1">
              <span className="block font-heading font-bold text-[#172A3A]">Les clubs sportifs & culturels</span>
              <span className="block text-sm text-slate-500">Trouvez un club près de chez vous</span>
            </span>
            <ArrowRight size={16} className="text-[#E05638] shrink-0 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </div>
      </div>
    </section>
  );
}
